
import React, { useState, useRef, useEffect } from "react";
import "./forget.css";
import { TextField, Button, InputAdornment, IconButton, CircularProgress } from "@mui/material";
import { Email, Visibility, VisibilityOff, Lock, CheckCircle } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const api = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL || "https://api.rismicmart.com",
  headers: { 
    "Content-Type": "application/json" 
  },
  timeout: 30000, 
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      console.error("API Error:", error.response.data);
      return Promise.reject(error);
    } else if (error.request) {
      console.error("Network Error: No response received");
      return Promise.reject(new Error("Network error. Please check your connection."));
    } else {
      console.error("Request Error:", error.message);
      return Promise.reject(error);
    }
  }
);

// Email validation regex
const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// Helper OTP array
const makeOtpArray = (n) => Array.from({ length: n }, () => "");

const Forget = () => {
  const navigate = useNavigate();

  const [stage, setStage] = useState("enterEmail");

  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const OTP_LENGTH = 6;
  const [otp, setOtp] = useState(makeOtpArray(OTP_LENGTH));
  const inputsRef = useRef([]);
  const [otpTimer, setOtpTimer] = useState(0);
  const otpTimerRef = useRef(null);
  const [resendCount, setResendCount] = useState(0);
  const [attemptsLeft, setAttemptsLeft] = useState(5);
  const isAutoSubmitting = useRef(false);

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    return () => {
      if (otpTimerRef.current) clearInterval(otpTimerRef.current);
    };
  }, []);

  const startOtpTimer = (seconds = 600) => { // 10 minutes = 600 seconds
    setOtpTimer(seconds);
    if (otpTimerRef.current) clearInterval(otpTimerRef.current);
    otpTimerRef.current = setInterval(() => {
      setOtpTimer((s) => {
        if (s <= 1) {
          clearInterval(otpTimerRef.current);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  };

  // ---------- API calls ----------
  const sendOtpRequest = async () => {
    setError("");
    setSuccessMessage("");
    
    if (!email || !validateEmail(email)) {
      setError("Please enter a valid email address.");
      return;
    }
    
    try {
      setLoading(true);
      console.log("Sending OTP to:", email);
      
      const res = await api.post("/api/delivery-person/forgot/send-otp", { 
        email: email.toLowerCase().trim() 
      });
      
      console.log("Send OTP response:", res.data);
      
      if (res.data && res.data.success) {
        setSuccessMessage("OTP sent successfully! Check your email.");
        setStage("enterOtp");
        setOtp(makeOtpArray(OTP_LENGTH));
        setAttemptsLeft(5);
        isAutoSubmitting.current = false; // Reset auto-submit flag
        setTimeout(() => {
          inputsRef.current[0]?.focus();
        }, 100);
        startOtpTimer(600); // 10 minutes
      } else {
        setError(res.data?.message || "Failed to send OTP");
      }
    } catch (err) {
      console.error("Send OTP error:", err);
      
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else if (err.message) {
        setError(err.message);
      } else {
        setError("Network error. Please check your connection and try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const resendOtp = async () => {
    setError("");
    setSuccessMessage("");
    setResendCount((c) => c + 1);
    setOtp(makeOtpArray(OTP_LENGTH));
    isAutoSubmitting.current = false; // Reset auto-submit flag
    await sendOtpRequest();
  };

  const verifyOtpRequest = async (otpString) => {
    // Prevent duplicate submissions
    if (isAutoSubmitting.current || loading) {
      console.log("Already submitting, skipping...");
      return;
    }

    setError("");
    setSuccessMessage("");
    
    if (!otpString || otpString.length !== OTP_LENGTH) {
      setError("Please enter the full 6-digit code.");
      return;
    }
    
    try {
      isAutoSubmitting.current = true; // Set flag to prevent duplicate calls
      setLoading(true);
      console.log("Verifying OTP for:", email);
      
      const res = await api.post("/api/delivery-person/forgot/verify-otp", { 
        email: email.toLowerCase().trim(),
        otp: otpString 
      });
      
      console.log("Verify OTP response:", res.data);
      
      if (res.data && res.data.success) {
        setSuccessMessage("OTP verified successfully!");
        setTimeout(() => {
          setStage("resetPassword");
          setSuccessMessage("");
          isAutoSubmitting.current = false; // Reset flag
        }, 1000);
      } else {
        setError(res.data?.message || "Invalid OTP");
        if (res.data?.attemptsLeft !== undefined) {
          setAttemptsLeft(res.data.attemptsLeft);
        }
        isAutoSubmitting.current = false; // Reset flag on error
      }
    } catch (err) {
      console.error("Verify OTP error:", err);
      
      if (err.response?.data?.message) {
        setError(err.response.data.message);
        if (err.response.data.attemptsLeft !== undefined) {
          setAttemptsLeft(err.response.data.attemptsLeft);
        }
      } else if (err.message) {
        setError(err.message);
      } else {
        setError("OTP verification failed. Please try again.");
      }
      isAutoSubmitting.current = false; // Reset flag on error
    } finally {
      setLoading(false);
    }
  };

  const resetPasswordRequest = async () => {
    setError("");
    setSuccessMessage("");
    
    if (!password || password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    
    try {
      setLoading(true);
      console.log("Resetting password for:", email);
      
      const res = await api.put("/api/delivery-person/forgot/reset", { 
        email: email.toLowerCase().trim(),
        password 
      });
      
      console.log("Reset password response:", res.data);
      
      if (res.data && res.data.success) {
        setSuccessMessage("Password reset successful!");
        setStage("done");
        setTimeout(() => navigate("/"), 2000);
      } else {
        setError(res.data?.message || "Failed to reset password");
      }
    } catch (err) {
      console.error("Reset password error:", err);
      
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else if (err.message) {
        setError(err.message);
      } else {
        setError("Password reset failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  // ---------- OTP input handlers ----------
  const handleOtpChange = (value, idx) => {
    if (!/^[0-9]?$/.test(value)) return; // only digits
    
    const next = [...otp];
    next[idx] = value;
    setOtp(next);

    // Auto-focus next input
    if (value && idx < OTP_LENGTH - 1) {
      inputsRef.current[idx + 1]?.focus();
    }
    
    // Auto-submit if all 6 digits are filled
    if (value && idx === OTP_LENGTH - 1) {
      const filledOtp = [...next];
      const isComplete = filledOtp.every(digit => digit !== "" && /^\d$/.test(digit));
      
      if (isComplete) {
        const otpString = filledOtp.join("");
        console.log("OTP Complete:", otpString);
        
        // Auto-submit after small delay for better UX
        setTimeout(() => {
          verifyOtpRequest(otpString);
        }, 300);
      }
    }
  };

  const handleOtpKeyDown = (e, idx) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      const next = [...otp];
      
      if (otp[idx] === "") {
        // Move to previous input if current is empty
        if (idx > 0) {
          inputsRef.current[idx - 1]?.focus();
        }
      } else {
        // Clear current input
        next[idx] = "";
        setOtp(next);
      }
    } else if (e.key === "ArrowLeft" && idx > 0) {
      e.preventDefault();
      inputsRef.current[idx - 1]?.focus();
    } else if (e.key === "ArrowRight" && idx < OTP_LENGTH - 1) {
      e.preventDefault();
      inputsRef.current[idx + 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").trim();
    
    // Check if pasted data is valid (only digits)
    if (!/^\d+$/.test(pastedData)) {
      setError("Please paste only numbers");
      return;
    }
    
    // Take first 6 digits
    const digits = pastedData.slice(0, OTP_LENGTH).split("");
    const newOtp = [...makeOtpArray(OTP_LENGTH)];
    
    digits.forEach((digit, idx) => {
      if (idx < OTP_LENGTH) {
        newOtp[idx] = digit;
      }
    });
    
    setOtp(newOtp);
    
    // If 6 digits pasted, auto-submit
    if (digits.length === OTP_LENGTH) {
      const otpString = newOtp.join("");
      console.log("OTP Pasted:", otpString);
      
      setTimeout(() => {
        verifyOtpRequest(otpString);
      }, 300);
    } else {
      // Focus last filled input or last input
      const lastFilledIndex = Math.min(digits.length - 1, OTP_LENGTH - 1);
      setTimeout(() => {
        inputsRef.current[lastFilledIndex]?.focus();
      }, 0);
    }
  };

  // Format timer display
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${String(secs).padStart(2, "0")}`;
  };

  const renderEmailStage = () => (
    <div className="forget-card">
      <h2 className="forget-title">Forgot password</h2>
      <p className="forget-sub">Enter your email address to receive a verification code</p>

      <TextField
        label="Email Address"
        variant="outlined"
        fullWidth
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value.trim())}
        onKeyPress={(e) => {
          if (e.key === 'Enter' && !loading) {
            sendOtpRequest();
          }
        }}
        disabled={loading}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Email style={{ color: "#007b68" }} />
            </InputAdornment>
          ),
        }}
        className="forget-input"
        autoFocus
      />

      {error && <div className="forget-error">{error}</div>}
      {successMessage && <div className="forget-success">{successMessage}</div>}

      <Button
        variant="contained"
        fullWidth
        className="forget-btn"
        onClick={sendOtpRequest}
        disabled={loading}
      >
        {loading ? (
          <>
            <CircularProgress size={20} style={{ marginRight: 8, color: "white" }} />
            Sending...
          </>
        ) : (
          "Send OTP"
        )}
      </Button>

      <div className="forget-links">
        <span onClick={() => !loading && navigate("/")}>Back to login</span>
      </div>
    </div>
  );

  const renderOtpStage = () => (
    <div className="forget-card">
      <h2 className="forget-title">Enter OTP</h2>
      <p className="forget-sub">We sent a 6-digit code to <strong>{email}</strong></p>

      <div className="otp-row" onPaste={handleOtpPaste}>
        {otp.map((digit, idx) => (
          <input
            key={idx}
            ref={(el) => (inputsRef.current[idx] = el)}
            type="text"
            inputMode="numeric"
            className="otp-input"
            maxLength={1}
            value={digit}
            onChange={(e) => handleOtpChange(e.target.value, idx)}
            onKeyDown={(e) => handleOtpKeyDown(e, idx)}
            disabled={loading}
          />
        ))}
      </div>

      {error && (
        <div className="forget-error">
          {error}
          {attemptsLeft > 0 && attemptsLeft < 5 && (
            <div style={{ fontSize: '12px', marginTop: '5px' }}>
              {attemptsLeft} {attemptsLeft === 1 ? 'attempt' : 'attempts'} remaining
            </div>
          )}
        </div>
      )}
      {successMessage && <div className="forget-success">{successMessage}</div>}

      <div className="otp-meta">
        {otpTimer > 0 ? (
          <div className="timer-text">
            ⏰ OTP expires in <strong>{formatTime(otpTimer)}</strong>
          </div>
        ) : (
          <div className="timer-expired">
            OTP expired. Please request a new one.
          </div>
        )}
      </div>

      {otpTimer === 0 ? (
        <Button
          variant="outlined"
          fullWidth
          className="resend-btn-full"
          onClick={resendOtp}
          disabled={loading}
        >
          {loading ? "Sending..." : "Resend OTP"}
        </Button>
      ) : (
        <Button
          variant="contained"
          fullWidth
          className="forget-btn"
          onClick={() => verifyOtpRequest(otp.join(""))}
          disabled={loading || otp.some(digit => digit === "")}
        >
          {loading ? (
            <>
              <CircularProgress size={20} style={{ marginRight: 8, color: "white" }} />
              Verifying...
            </>
          ) : (
            "Verify OTP"
          )}
        </Button>
      )}

      <div className="forget-links">
        <span onClick={() => !loading && setStage("enterEmail")}>Change email</span>
        {otpTimer > 0 && (
          <>
            <span style={{ margin: '0 8px', color: '#ccc' }}>•</span>
            <span onClick={resendOtp} style={{ opacity: loading ? 0.5 : 1 }}>
              Resend OTP ({resendCount})
            </span>
          </>
        )}
      </div>
    </div>
  );

  const renderResetStage = () => (
    <div className="forget-card">
      <h2 className="forget-title">Set new password</h2>
      <p className="forget-sub">Create a secure password for your account</p>

      <TextField
        label="New password"
        variant="outlined"
        type={showPassword ? "text" : "password"}
        fullWidth
        className="forget-input"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        disabled={loading}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Lock style={{ color: "#007b68" }} />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={() => setShowPassword((s) => !s)} disabled={loading}>
                {showPassword ? (
                  <Visibility style={{ color: "#007b68" }} />
                ) : (
                  <VisibilityOff style={{ color: "#007b68" }} />
                )}
              </IconButton>
            </InputAdornment>
          ),
        }}
        autoFocus
      />

      <TextField
        label="Confirm password"
        variant="outlined"
        type={showConfirm ? "text" : "password"}
        fullWidth
        className="forget-input"
        value={confirm}
        onChange={(e) => setConfirm(e.target.value)}
        onKeyPress={(e) => {
          if (e.key === 'Enter' && !loading) {
            resetPasswordRequest();
          }
        }}
        disabled={loading}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Lock style={{ color: "#007b68" }} />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={() => setShowConfirm((s) => !s)} disabled={loading}>
                {showConfirm ? (
                  <Visibility style={{ color: "#007b68" }} />
                ) : (
                  <VisibilityOff style={{ color: "#007b68" }} />
                )}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <div className="password-requirements">
        <small>Password must be at least 6 characters long</small>
      </div>

      {error && <div className="forget-error">{error}</div>}
      {successMessage && <div className="forget-success">{successMessage}</div>}

      <Button
        variant="contained"
        fullWidth
        className="forget-btn"
        onClick={resetPasswordRequest}
        disabled={loading}
      >
        {loading ? (
          <>
            <CircularProgress size={20} style={{ marginRight: 8, color: "white" }} />
            Resetting...
          </>
        ) : (
          "Reset password"
        )}
      </Button>

      <div className="forget-links">
        <span onClick={() => !loading && setStage("enterEmail")}>Cancel</span>
      </div>
    </div>
  );

  const renderDoneStage = () => (
    <div className="forget-card">
      <div className="success-icon">
        <CheckCircle style={{ fontSize: 64, color: "#007b68" }} />
      </div>
      <h2 className="forget-title">Password Reset Successful!</h2>
      <p className="forget-sub">
        Your password has been reset successfully. <br />
        Redirecting to login...
      </p>
      <div className="loading-dots">
        <CircularProgress style={{ color: "#007b68" }} />
      </div>
    </div>
  );

  return (
    <div className="forget-container">
      {stage === "enterEmail" && renderEmailStage()}
      {stage === "enterOtp" && renderOtpStage()}
      {stage === "resetPassword" && renderResetStage()}
      {stage === "done" && renderDoneStage()}
    </div>
  );
};

export default Forget;

